// AI Gap & Risk Intelligence API (cara_backend/docs/GAP_INTELLIGENCE_LLD.md §11).
// Callers handle errors; a 404 means the tenant is not in the pilot or the record is not visible.
import api from '../api/axios';

const BASE = '/api/gap-intelligence';

const dropEmpty = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && !v.length)));

// Array filters go as repeated keys (status=A&status=B), which DRF ListFields read.
const listParams = (params) => ({ params: dropEmpty(params), paramsSerializer: { indexes: null } });

const taskUrl = (taskId, suffix = '') => `${BASE}/tasks/${encodeURIComponent(taskId)}/${suffix}`;

const gapIntelligenceService = {
  isEnabled: async () => (await api.get(`${BASE}/enabled/`)).data?.data?.enabled === true,

  startJob: async ({ frameworkKey, cid, language }) =>
    (await api.post(`${BASE}/ai/jobs/`, { framework_key: frameworkKey, cid: String(cid), language })).data.data,

  getLatestJob: async ({ frameworkKey, cid, feature = 'gap_analysis' }) =>
    (await api.get(`${BASE}/ai/jobs/latest/`, { params: { framework_key: frameworkKey, cid, feature } })).data.data,

  // Draft jobs (F3 tasks, F2 report, F4 risks, F5 treatment) - LLD §7.3.
  startTaskJob: async ({ gapIds, language }) =>
    (await api.post(`${BASE}/ai/task-jobs/`, { gap_ids: gapIds, language })).data.data,

  startReportJob: async ({ frameworkKey, cid, language }) =>
    (await api.post(`${BASE}/ai/report-jobs/`, { framework_key: frameworkKey, cid: String(cid), language })).data.data,

  startRiskJob: async ({ frameworkKey, cid, language }) =>
    (await api.post(`${BASE}/ai/risk-jobs/`, { framework_key: frameworkKey, cid: String(cid), language })).data.data,

  startTreatmentJob: async ({ riskId, language }) =>
    (await api.post(`${BASE}/ai/treatment-jobs/`, { risk_id: riskId, language })).data.data,

  getJob: async (jobId) => (await api.get(`${BASE}/ai/jobs/${encodeURIComponent(jobId)}/`)).data.data,

  getDrafts: async (jobId) => (await api.get(`${BASE}/ai/jobs/${encodeURIComponent(jobId)}/drafts/`)).data.data,

  resumeJob: async (jobId) => (await api.post(`${BASE}/ai/jobs/${encodeURIComponent(jobId)}/resume/`)).data.data,

  commit: async (jobId, { idempotencyKey, decisions }) =>
    (await api.post(`${BASE}/ai/jobs/${encodeURIComponent(jobId)}/commit/`, {
      idempotency_key: idempotencyKey,
      decisions,
    })).data.data,

  listGaps: async (params) => {
    const { data } = await api.get(`${BASE}/gaps/`, { params: dropEmpty(params) });
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  getGap: async (gapId) => (await api.get(`${BASE}/gaps/${encodeURIComponent(gapId)}/`)).data.data,

  assignGap: async (gapId, { ownerId, validatorId, targetDate }) =>
    (await api.post(`${BASE}/gaps/${encodeURIComponent(gapId)}/assign/`, {
      owner_id: ownerId, validator_id: validatorId, target_date: targetDate,
    })).data.data,

  // ``controls``: [{framework_key, cid, control_id, status, notes}] - the updated
  // control responses a closure submits (owner) or corrects (validator).
  transitionGap: async (gapId, { action, comment, controls }) =>
    (await api.post(`${BASE}/gaps/${encodeURIComponent(gapId)}/transition/`, {
      action, comment, ...(controls ? { controls } : {}),
    })).data.data,

  // Manual gaps (LLD §9.4).
  createGap: async (body) => (await api.post(`${BASE}/gaps/`, body)).data.data,

  editGapSource: async (gapId, { action, frameworkKey, cid, controlId }) =>
    (await api.post(`${BASE}/gaps/${encodeURIComponent(gapId)}/sources/`, {
      action, framework_key: frameworkKey, cid: String(cid), control_id: controlId,
    })).data.data,

  // Closure form: each control's live rating + the owner's proposal.
  getGapClosure: async (gapId, language) =>
    (await api.get(`${BASE}/gaps/${encodeURIComponent(gapId)}/closure/`, { params: { language } })).data.data,

  // Control picker: a framework's assessments, or one assessment's controls.
  listFrameworkAssessments: async (frameworkKey) =>
    (await api.get(`${BASE}/framework-assessments/`, { params: { framework_key: frameworkKey } })).data.data || [],

  getAssessmentControls: async ({ frameworkKey, cid, language }) =>
    (await api.get(`${BASE}/framework-assessments/`, {
      params: { framework_key: frameworkKey, cid: String(cid), language },
    })).data.data,

  // {control_id: gap_id} of the framework's controls already on an open gap in one BU + location.
  getTakenControls: async ({ frameworkKey, businessUnit, location }) =>
    (await api.get(`${BASE}/framework-assessments/taken-controls/`, {
      params: { framework_key: frameworkKey, business_unit: businessUnit || '', location: location || '' },
    })).data.data || {},

  // In-scope people for one role (LLD §13): [{id, name}].
  getOwners: async ({ businessUnit, location, purpose }) =>
    (await api.get(`${BASE}/owners/`, {
      params: { business_unit: businessUnit || '', location: location || '', purpose },
    })).data.data || [],

  // Approved AI narrative for one assessment, or null (LLD §7.3 F2).
  getReport: async ({ frameworkKey, cid }) =>
    (await api.get(`${BASE}/reports/`, { params: { framework_key: frameworkKey, cid } })).data.data,

  getReportHistory: async ({ frameworkKey, cid }) =>
    (await api.get(`${BASE}/reports/history/`, { params: { framework_key: frameworkKey, cid } })).data.data,

  listTasks: async (params) => {
    const { data } = await api.get(`${BASE}/tasks/`, listParams(params));
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  getTask: async (taskId) => (await api.get(`${BASE}/tasks/${encodeURIComponent(taskId)}/`)).data.data,

  // Manual task on gaps and / or risks of one BU + location.
  createTask: async ({ title, description, priority, dueDate, ownerId, reviewerId, gapIds = [], riskIds = [] }) =>
    (await api.post(`${BASE}/tasks/`, {
      title, description, priority, due_date: dueDate, owner_id: ownerId, reviewer_id: reviewerId,
      gap_ids: gapIds, risk_ids: riskIds,
    })).data.data,

  // ``comment`` is the closing comment on Close and the reason on approve / reject / cancel.
  transitionTask: async (taskId, { status, comment }) =>
    (await api.post(taskUrl(taskId, 'transition/'), { status, comment })).data.data,

  // Action Tracker v2 (LLD §20).
  getTaskFilterOptions: async () => (await api.get(`${BASE}/tasks/filter-options/`)).data.data,

  getTaskSummary: async (params) => (await api.get(`${BASE}/tasks/summary/`, listParams(params))).data.data,

  // Every row matching the filters (server cap 5,000 -> 400 export_too_large).
  exportTasks: async (params) => (await api.get(`${BASE}/tasks/export/`, listParams(params))).data.data,

  listActivity: async (params) => {
    const { data } = await api.get(`${BASE}/tasks/activity/`, listParams(params));
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  exportActivity: async (params) => (await api.get(`${BASE}/tasks/activity/export/`, listParams(params))).data.data,

  listEvidence: async (params) => {
    const { data } = await api.get(`${BASE}/tasks/evidence/`, listParams(params));
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  updateTask: async (taskId, fields) => (await api.patch(taskUrl(taskId), fields)).data.data,

  reassignTask: async (taskId, { ownerId, reviewerId, reason }) =>
    (await api.post(taskUrl(taskId, 'reassign/'), dropEmpty({ owner_id: ownerId, reviewer_id: reviewerId, reason }))).data.data,

  changeTaskDueDate: async (taskId, { dueDate, reason }) =>
    (await api.post(taskUrl(taskId, 'due-date/'), { due_date: dueDate, reason })).data.data,

  changeTaskLink: async (taskId, { action, linkType, linkId }) =>
    (await api.post(taskUrl(taskId, 'links/'), { action, link_type: linkType, link_id: linkId })).data.data,

  addTaskComment: async (taskId, comment) => (await api.post(taskUrl(taskId, 'comments/'), { comment })).data.data,

  getTaskActivity: async (taskId, params) => {
    const { data } = await api.get(taskUrl(taskId, 'activity/'), listParams(params));
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  // Evidence needs the comment that explains it (In Progress tasks only).
  uploadTaskEvidence: async (taskId, files, comment) => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    form.append('comment', comment);
    return (await api.post(taskUrl(taskId, 'evidence/'), form)).data.data;
  },

  // PDF / image evidence as a Blob for an in-browser preview.
  previewTaskEvidence: async (taskId, evidenceId) =>
    (await api.get(taskUrl(taskId, `evidence/${encodeURIComponent(evidenceId)}/download/`), {
      params: { inline: 1 }, responseType: 'blob',
    })).data,

  listRisks: async (params) => {
    const { data } = await api.get(`${BASE}/risks/`, { params: dropEmpty(params) });
    return { rows: data.data || [], pagination: data.pagination || {} };
  },

  getRisk: async (riskId) => (await api.get(`${BASE}/risks/${encodeURIComponent(riskId)}/`)).data.data,

  // Manual risk on open gaps (the rating band is computed by the server).
  createRisk: async (body) => (await api.post(`${BASE}/risks/`, body)).data.data,

  riskAction: async (riskId, body) =>
    (await api.post(`${BASE}/risks/${encodeURIComponent(riskId)}/action/`, body)).data.data,
};

export default gapIntelligenceService;
